import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { appStatus } from "../../store/modules/app_status/actions";
import PageHeader from "../page_header";
import AreaCard from "./ListAreas/AreaCard";
import AlertRequestsPanel from "./AlertRequestsPanel";
import UnansweredAlertsPanel from "./UnansweredAlertsPanel";
import { UNANSWERED_ALERTS_TAB } from "../../utils/alertNotificationsReview";
import { TableAlertTickets } from "./ViewAlerts/DynamicTable/TableAlertTickets";
import AlertCodeSearchBar from "./ViewAlerts/AlertCodeSearchBar";
import { guardianAreaService } from "../../services/guardianAreaService";
import { guardianAlertService } from "../../services/guardianAlertService";
import {
  canAccessAlertRequests,
  canAccessGuardianReportsTab,
  canAccessInviteWelcome,
  canCreateGuardianAlert,
  canReviewAlertNotifications,
} from "../../utils/userRoles";
import {
  MainContainer,
  ContentWrapper,
  ButtonGrid,
  ActionButton,
  ButtonIcon,
  ButtonText,
  ButtonDescription,
  BackgroundPattern,
  TabPanel,
  TabHint,
  TabToolbar,
  TabActionButton,
  LoadingText,
} from "./styles";
import {
  FormCard,
  FormRow,
  Label,
  Input,
  Hint,
  SubmitButton,
  Feedback,
  OutcomeBadge,
  MetaList,
  PageIntro,
  EmailStatusBanner,
  SecondaryLink,
} from "./InviteWelcome/styles";
import {
  guardianInviteService,
  INVITE_OUTCOME_LABELS,
} from "../../services/guardianInviteService";
import { goToPanel } from "../../utils/appNavigation";
import { FormNewAlert } from "./FormNewAlert";
import AlertReportHistory from "./HistoryAlert/AlertReportHistory/AlertreportHistory";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const tabMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
};

function resolveEmailDelivery(result) {
  if (!result || typeof result !== "object") {
    return { status: "unknown", title: "Status do e-mail não informado", detail: null };
  }
  if (result.email_sent === true) {
    return {
      status: "sent",
      title: "E-mail de boas-vindas enviado com sucesso",
      detail: result.email_error ? null : "Confirmado pelo backend (SMTP).",
    };
  }
  if (result.email_sent === false) {
    return {
      status: "failed",
      title: "E-mail de boas-vindas NÃO foi enviado",
      detail:
        result.email_error ||
        result.email_send_error ||
        "O cadastro/identificação pode ter ocorrido, mas o envio SMTP falhou.",
    };
  }
  return {
    status: "unknown",
    title: "Não foi possível confirmar se o e-mail foi enviado",
    detail: "A API não retornou o campo email_sent.",
  };
}

function AreasTab() {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await guardianAreaService.getGuardianAreas();
        if (!cancelled) setAreas(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erro ao carregar áreas:", err);
        if (!cancelled) setAreas([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <TabToolbar>
        <TabActionButton type="button" onClick={() => navigate("/create_area")}>
          Nova área
        </TabActionButton>
        <TabActionButton
          type="button"
          $secondary
          onClick={() => navigate("/archived_areas")}
        >
          Áreas arquivadas
        </TabActionButton>
        <TabActionButton
          type="button"
          $secondary
          onClick={() => navigate("/areas")}
        >
          Abrir lista completa
        </TabActionButton>
      </TabToolbar>
      {loading ? (
        <LoadingText>Carregando áreas...</LoadingText>
      ) : (
        <AreaCard filteredAreas={areas} />
      )}
    </>
  );
}

function AlertsTab() {
  const navigate = useNavigate();
  const [alertTickets, setAlertTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await guardianAlertService.getCompleteAlerts();
        if (!cancelled) setAlertTickets(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erro ao carregar alertas:", err);
        if (!cancelled) setAlertTickets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <TabToolbar>
        <TabActionButton
          type="button"
          $secondary
          onClick={() => navigate("/view_alerts")}
        >
          Abrir tela completa
        </TabActionButton>
      </TabToolbar>
      <AlertCodeSearchBar />
      {loading ? (
        <LoadingText>Carregando alertas...</LoadingText>
      ) : alertTickets.length > 0 ? (
        <TableAlertTickets alertTickets={alertTickets} />
      ) : (
        <TabHint>Não há alertas para exibir no momento.</TabHint>
      )}
    </>
  );
}

function InviteTab() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Informe um e-mail válido.");
      return;
    }
    setSending(true);
    setError("");
    setResult(null);
    try {
      const data = await guardianInviteService.sendWelcome({ email: trimmed });
      setResult({ success: true, ...data, email: trimmed });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Falha ao enviar convite."
      );
      setResult({ success: false });
    } finally {
      setSending(false);
    }
  };

  const emailDelivery = resolveEmailDelivery(result);
  const outcomeLabel =
    INVITE_OUTCOME_LABELS[result?.outcome] || result?.outcome || "Processado";
  const feedbackVariant =
    emailDelivery.status === "failed"
      ? "error"
      : emailDelivery.status === "unknown"
        ? "warning"
        : result?.success
          ? "warning"
          : "success";

  return (
    <>
      <PageIntro>
        Envie um e-mail de boas-vindas para um Guardian com o link do app e o
        manual em PDF.
      </PageIntro>
      <FormCard onSubmit={handleSubmit}>
        <FormRow>
          <Label htmlFor="guardian-invite-email">E-mail do destinatário</Label>
          <Input
            id="guardian-invite-email"
            type="email"
            autoComplete="email"
            placeholder="pessoa@dominio.com"
            value={email}
            disabled={sending}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Hint>
            Se o usuário não existir, um Guardian será criado só com o e-mail
            (senha temporária no e-mail).
          </Hint>
        </FormRow>
        <SubmitButton type="submit" disabled={sending}>
          {sending ? "Enviando…" : "Enviar boas-vindas"}
        </SubmitButton>
        {error ? (
          <Feedback $variant="error" role="alert">
            {error}
          </Feedback>
        ) : null}
        {result?.success ? (
          <Feedback $variant={feedbackVariant} role="status">
            <EmailStatusBanner $status={emailDelivery.status}>
              {emailDelivery.title}
              {emailDelivery.detail ? <span>{emailDelivery.detail}</span> : null}
            </EmailStatusBanner>
            <div>
              <OutcomeBadge $outcome={result.outcome}>{outcomeLabel}</OutcomeBadge>
              {result.how_to_find?.hint ||
                result.message ||
                "Convite processado com sucesso."}
            </div>
            <MetaList>
              <li>E-mail: {result.email}</li>
              <li>Tabela: {result.table || "—"}</li>
            </MetaList>
            <SecondaryLink
              type="button"
              onClick={() =>
                goToPanel(dispatch, navigate, "Usuários", {
                  state: {
                    activeTab:
                      result.outcome === "created_user_request"
                        ? "requests"
                        : "users",
                  },
                })
              }
            >
              {result.outcome === "created_user_request"
                ? "Abrir solicitações de cadastro →"
                : "Abrir usuários →"}
            </SecondaryLink>
          </Feedback>
        ) : null}
      </FormCard>
    </>
  );
}

const Guardian = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);
  const showCreateAlert = canCreateGuardianAlert(currentUser);
  const showInviteWelcome = canAccessInviteWelcome(currentUser);
  const showAlertRequests = canAccessAlertRequests(currentUser);
  const showReportsTab = canAccessGuardianReportsTab(currentUser);
  const showUnansweredAlerts = canReviewAlertNotifications(currentUser);

  const tabs = useMemo(() => {
    const list = [
      { id: "panel", label: "Painel Central" },
      { id: "areas", label: "Áreas" },
      { id: "alerts", label: "Alertas" },
    ];
    if (showUnansweredAlerts) {
      list.push({ id: UNANSWERED_ALERTS_TAB, label: "Não respondidos" });
    }
    if (showAlertRequests) {
      list.push({ id: "requests", label: "Solicitações" });
    }
    if (showCreateAlert) {
      list.push({ id: "new_alert", label: "Novo Alerta" });
    }
    if (showReportsTab) {
      list.push({ id: "reports", label: "Relatórios" });
    }
    if (showInviteWelcome) {
      list.push({ id: "invite", label: "Boas-vindas" });
    }
    return list;
  }, [
    showAlertRequests,
    showCreateAlert,
    showInviteWelcome,
    showReportsTab,
    showUnansweredAlerts,
  ]);

  const allowedIds = useMemo(() => new Set(tabs.map((t) => t.id)), [tabs]);

  const initialTab = allowedIds.has(location.state?.activeTab)
    ? location.state.activeTab
    : "panel";
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const next = location.state?.activeTab;
    if (next && allowedIds.has(next)) {
      setActiveTab(next);
    }
  }, [location.state, allowedIds]);

  useEffect(() => {
    if (!allowedIds.has(activeTab)) {
      setActiveTab("panel");
    }
  }, [allowedIds, activeTab]);

  const openTab = (tabId) => {
    if (!allowedIds.has(tabId)) return;
    setActiveTab(tabId);
  };

  const handleNewAlertTab = () => {
    openTab("new_alert");
  };

  return (
    <MainContainer>
      <BackgroundPattern />
      <ContentWrapper>
        <PageHeader
          title="Guardian"
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={openTab}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={tabMotion.initial}
            animate={tabMotion.animate}
            exit={tabMotion.exit}
            transition={tabMotion.transition}
            style={{ width: "100%" }}
          >
            <TabPanel $center={activeTab === "new_alert"}>
              {activeTab === "panel" && (
                <ButtonGrid>
                  <ActionButton type="button" onClick={() => openTab("areas")}>
                    <ButtonIcon>🗺️</ButtonIcon>
                    <ButtonText>Áreas</ButtonText>
                    <ButtonDescription>
                      Cadastrar, editar e visualizar áreas de interesse
                    </ButtonDescription>
                  </ActionButton>

                  {showCreateAlert && (
                    <ActionButton type="button" onClick={handleNewAlertTab}>
                      <ButtonIcon>🚨</ButtonIcon>
                      <ButtonText>Cadastrar novo Alerta</ButtonText>
                      <ButtonDescription>
                        Criar alertas de emergência
                      </ButtonDescription>
                    </ActionButton>
                  )}

                  <ActionButton type="button" onClick={() => openTab("alerts")}>
                    <ButtonIcon>📊</ButtonIcon>
                    <ButtonText>Alertas</ButtonText>
                    <ButtonDescription>
                      Monitorar alertas ativos
                    </ButtonDescription>
                  </ActionButton>

                  {showUnansweredAlerts && (
                    <ActionButton
                      type="button"
                      onClick={() => openTab(UNANSWERED_ALERTS_TAB)}
                    >
                      <ButtonIcon>📵</ButtonIcon>
                      <ButtonText>Alertas não respondidos</ButtonText>
                      <ButtonDescription>
                        Envios ativos e desligados, reativação e carga do
                        servidor
                      </ButtonDescription>
                    </ActionButton>
                  )}

                  {showAlertRequests && (
                    <ActionButton
                      type="button"
                      onClick={() => openTab("requests")}
                    >
                      <ButtonIcon>🔔</ButtonIcon>
                      <ButtonText>Solicitações</ButtonText>
                      <ButtonDescription>
                        Solicitações de alerta pendentes e resolvidas
                      </ButtonDescription>
                    </ActionButton>
                  )}

                  {showReportsTab && (
                    <ActionButton type="button" onClick={() => openTab("reports")}>
                      <ButtonIcon>📄</ButtonIcon>
                      <ButtonText>Relatórios</ButtonText>
                      <ButtonDescription>
                        Histórico de relatórios gerados por área
                      </ButtonDescription>
                    </ActionButton>
                  )}

                  {showInviteWelcome && (
                    <ActionButton type="button" onClick={() => openTab("invite")}>
                      <ButtonIcon>✉️</ButtonIcon>
                      <ButtonText>Boas-vindas</ButtonText>
                      <ButtonDescription>
                        Enviar e-mail de convite / boas-vindas e gerenciar links
                        do app
                      </ButtonDescription>
                    </ActionButton>
                  )}
                </ButtonGrid>
              )}

              {activeTab === "areas" && <AreasTab />}
              {activeTab === "alerts" && <AlertsTab />}
              {activeTab === UNANSWERED_ALERTS_TAB && showUnansweredAlerts && (
                <UnansweredAlertsPanel
                  loginAttention={location.state?.loginAttention || null}
                />
              )}
              {activeTab === "requests" && <AlertRequestsPanel />}
              {activeTab === "new_alert" && (
                <FormNewAlert
                  embedded
                  alertRequest={location.state?.alertRequest || null}
                  prefillAlert={location.state?.prefillAlert || null}
                  onCancel={() => openTab("panel")}
                  onSuccess={({ alertRequestId } = {}) => {
                    openTab(alertRequestId ? "requests" : "alerts");
                  }}
                />
              )}
              {activeTab === "reports" && showReportsTab && (
                <AlertReportHistory embedded />
              )}
              {activeTab === "invite" && <InviteTab />}
            </TabPanel>
          </motion.div>
        </AnimatePresence>
      </ContentWrapper>
    </MainContainer>
  );
};

export default Guardian;
